import {
  getDefaultRtmClaimsMonthId,
  getRtmClaimsMonthDateRange,
} from "./rtmClaims";

export const REPORT_EMPTY_DISPLAY = "--";
export const REPORT_TABLE_TEXT_MAX_LENGTH = 40;
export const FILTER_DROPDOWN_TEXT_MAX_LENGTH = 25;

/** Detect whether an element's visible text is clipped (ellipsis / line-clamp). */
export const isElementTextTruncated = (element) => {
  if (!element) return false;

  const style = window.getComputedStyle(element);

  if (style.whiteSpace === "nowrap") {
    return element.scrollWidth > element.clientWidth + 1;
  }

  const lineClampValue = style.getPropertyValue("-webkit-line-clamp") || style.webkitLineClamp;
  const lineClamp = parseInt(lineClampValue, 10);

  if (Number.isFinite(lineClamp) && lineClamp > 0) {
    const width = element.clientWidth;
    if (width <= 0) return false;

    const measure = document.createElement("div");
    measure.textContent = element.textContent;
    measure.style.cssText = [
      "position:absolute",
      "visibility:hidden",
      "pointer-events:none",
      "left:-9999px",
      "top:0",
      `width:${width}px`,
      `font:${style.font}`,
      `letter-spacing:${style.letterSpacing}`,
      `word-break:${style.wordBreak}`,
      `overflow-wrap:${style.overflowWrap}`,
      "white-space:normal",
      `line-height:${style.lineHeight}`,
    ].join(";");
    element.ownerDocument.body.appendChild(measure);
    const naturalHeight = measure.offsetHeight;
    measure.remove();

    return naturalHeight > element.clientHeight + 1;
  }

  return element.scrollHeight > element.clientHeight + 1
    || element.scrollWidth > element.clientWidth + 1;
};

/** Truncate report table text to maxLength chars; adds ellipsis when longer */
export const truncateReportTableText = (text, maxLength = REPORT_TABLE_TEXT_MAX_LENGTH) => {
  const value = text == null ? "" : String(text);
  if (value.length <= maxLength) {
    return { display: value, full: value, truncated: false };
  }
  return {
    display: `${value.slice(0, maxLength)}...`,
    full: value,
    truncated: true,
  };
};

/** Truncate filter dropdown / pill text to maxLength chars with ellipsis. */
export const truncateFilterDropdownText = (text, maxLength = FILTER_DROPDOWN_TEXT_MAX_LENGTH) =>
  truncateReportTableText(text, maxLength);

/** Up to 2 decimal places; whole numbers without trailing zeros (e.g. 1.89, 10) */
export const formatReportDecimalValue = (value, maxDecimals = 2) => {
  if (value == null || value === "") return null;
  const num = Number(value);
  if (Number.isNaN(num)) return null;

  const factor = 10 ** maxDecimals;
  const rounded = Math.round(num * factor) / factor;
  if (Number.isInteger(rounded)) {
    return String(rounded);
  }
  return rounded.toFixed(maxDecimals).replace(/\.?0+$/, "");
};

export const formatReportPercent = (value, fallback = "--", maxDecimals = 2) => {
  const formatted = formatReportDecimalValue(value, maxDecimals);
  return formatted != null ? `${formatted}%` : fallback;
};

export const formatReportHours = (value, fallback = "--") => {
  const formatted = formatReportDecimalValue(value);
  return formatted != null ? `${formatted} hrs` : fallback;
};

export const formatReportCountPercent = (count, pct, fallback = "--") => {
  if (count == null || pct == null) return fallback;
  const pctLabel = formatReportPercent(pct, null);
  if (pctLabel == null) return fallback;
  return `${Number(count).toLocaleString()} (${pctLabel})`;
};

/** Shared adherence band colors — green (high), orange (middle), red (low). */
export const ADHERENCE_TIER_COLORS = {
  high: { text: "#2e7d32", background: "#e8f5e9" },
  middle: { text: "#ef6c00", background: "#fff3e0" },
  low: { text: "#c62828", background: "#ffebee" },
};

export const ADHERENCE_BAND_COLORS = {
  max: ADHERENCE_TIER_COLORS.high.text,
  middle: ADHERENCE_TIER_COLORS.middle.text,
  low: ADHERENCE_TIER_COLORS.low.text,
};

/** Classify an adherence rate into high / middle / low using min/max thresholds. */
export const getAdherenceTier = (rate, minThreshold, maxThreshold) => {
  const num = Number(rate);
  if (Number.isNaN(num)) return null;
  if (maxThreshold != null && num >= maxThreshold) return "high";
  if (minThreshold != null && num <= minThreshold) return "low";
  if (minThreshold != null && maxThreshold != null) return "middle";
  return null;
};

/** Middle adherence band label from min/max thresholds (e.g. "51 - 79% Adherence"). */
export const buildAdherenceMiddleLabel = (minThreshold, maxThreshold) => {
  const lowBound = minThreshold + 1;
  const highBound = maxThreshold - 1;
  if (lowBound > highBound) {
    return `${minThreshold}% - ${maxThreshold}% Adherence`;
  }
  return `${lowBound} - ${highBound}% Adherence`;
};

/**
 * Specific Range is stored per picker type and the two never overwrite each other:
 * `startDate`/`endDate` belong to the calendar pages, `month` belongs to RTM Claims.
 */
export const DEFAULT_REPORT_DASHBOARD_FILTERS = {
  customerIds: [],
  locationIds: [],
  providerIds: [],
  therapistId: null,
  accountType: "ALL",
  timeframe: "CURRENT",
  startDate: null,
  endDate: null,
  month: null,
};

export const isCustomTimeframeComplete = (selectedFilters = {}) => {
  if (selectedFilters.timeframe !== "CUSTOM") return true;
  // Either picker satisfies CUSTOM: calendar dates or an RTM month
  return !!((selectedFilters.startDate && selectedFilters.endDate) || selectedFilters.month);
};

/** Calendar pages ignore `month`; Specific Range needs its own date range there */
export const hasCalendarDateRange = (filters = {}) => (
  !!(filters.startDate && filters.endDate)
);

const normalizeFilterValueForCompare = (key, value, filters = {}) => {
  if (Array.isArray(value)) {
    return value.map((id) => (id != null && id !== "" ? String(id) : "")).filter(Boolean).sort();
  }
  if (key === "accountType") return value || "ALL";
  if (key === "timeframe") return value || "CURRENT";
  // Calendar dates / RTM month are only active while CUSTOM is selected
  if (key === "startDate" || key === "endDate" || key === "month") {
    if (filters.timeframe !== "CUSTOM") return null;
  }
  return value ?? null;
};

/** Compare draft vs applied (or vs defaults) for Apply/Reset enablement */
export const areReportFiltersEqual = (a = {}, b = {}) => {
  const keys = [
    "customerIds",
    "locationIds",
    "providerIds",
    "therapistId",
    "accountType",
    "timeframe",
    "startDate",
    "endDate",
    "month",
  ];
  return keys.every((key) => (
    JSON.stringify(normalizeFilterValueForCompare(key, a[key], a))
    === JSON.stringify(normalizeFilterValueForCompare(key, b[key], b))
  ));
};

export const canFetchReportDashboardData = (state) => {
  const therapistId = state.user?.profile?.id;
  if (!therapistId) return false;
  const applied = state.reportDashboard?.appliedFilters
    ?? state.reportDashboard?.selectedFilters;
  return isCustomTimeframeComplete(applied);
};

/** Map date-picker selection to UTC start/end using calendar Y-M-D (avoids timezone day shift) */
export const pickerDateToUtcStart = (date) => {
  if (!date) return null;
  const y = date.getFullYear();
  const m = date.getMonth();
  const d = date.getDate();
  return new Date(Date.UTC(y, m, d, 0, 0, 0, 0)).toISOString();
};

export const pickerDateToUtcEnd = (date) => {
  if (!date) return null;
  const y = date.getFullYear();
  const m = date.getMonth();
  const d = date.getDate();
  return new Date(Date.UTC(y, m, d, 23, 59, 59, 999)).toISOString();
};

export const utcIsoToPickerDate = (iso) => {
  if (!iso) return null;
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Date(parsed.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate());
};

export const formatFilterDateLabel = (iso) => {
  const date = utcIsoToPickerDate(iso);
  if (!date) return "Select date";
  return date.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });
};

const padReportDatePart = (value) => String(value).padStart(2, "0");

export const formatReportDashboardDate = (value) => {
  if (value == null || value === "") return null;

  const trimmed = String(value).trim();
  if (trimmed === "-" || trimmed === "—" || trimmed === "--") return null;

  const slashMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slashMatch) {
    const [, month, day, year] = slashMatch;
    return `${padReportDatePart(month)}-${padReportDatePart(day)}-${year}`;
  }

  const isoPart = trimmed.split("T")[0];
  const dashMatch = isoPart.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dashMatch) {
    const [, year, month, day] = dashMatch;
    return `${month}-${day}-${year}`;
  }

  const parsed = new Date(trimmed);
  if (!Number.isNaN(parsed.getTime())) {
    return `${padReportDatePart(parsed.getUTCMonth() + 1)}-${padReportDatePart(parsed.getUTCDate())}-${parsed.getUTCFullYear()}`;
  }

  return null;
};

/** Chart x-axis dates — mm/dd */
export const formatReportAxisMonthDay = (date) => {
  if (!date) return "";

  const trimmed = String(date).trim();
  const isoPart = trimmed.split("T")[0];

  const isoMatch = isoPart.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) {
    const [, , month, day] = isoMatch;
    return `${month}/${day}`;
  }

  const slashMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slashMatch) {
    const [, month, day] = slashMatch;
    return `${padReportDatePart(month)}/${padReportDatePart(day)}`;
  }

  return trimmed;
};

/**
 * Evenly spaced indexes across a series (includes first + last).
 * Used so Google Charts shows a fixed number of X labels while plotting every point.
 */
export const getReportEvenTickIndexes = (total, tickCount = 5) => {
  if (total <= 0) return [];
  if (total === 1) return [0];

  const safeTickCount = Math.min(Math.max(2, tickCount), total);
  const indexes = [];
  for (let i = 0; i < safeTickCount; i += 1) {
    indexes.push(Math.round((i * (total - 1)) / (safeTickCount - 1)));
  }
  return [...new Set(indexes)];
};

/**
 * Google Charts hAxis.ticks for a numeric X (point index) with date labels.
 * Prefer this over blank string categories — dense discrete axes hide all labels.
 */
export const getReportEvenAxisTicks = (points, tickCount = 5, getDate = (p) => p?.date) => {
  const total = Array.isArray(points) ? points.length : 0;
  return getReportEvenTickIndexes(total, tickCount).map((index) => ({
    v: index,
    f: formatReportAxisMonthDay(getDate(points[index])),
  }));
};

/** Chart tooltip heading — mm/dd/yyyy */
export const formatReportTooltipSlashDate = (date) => {
  if (!date) return "";

  const trimmed = String(date).trim();
  const isoPart = trimmed.split("T")[0];

  const isoMatch = isoPart.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    return `${month}/${day}/${year}`;
  }

  const slashMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slashMatch) {
    const [, month, day, year] = slashMatch;
    return `${padReportDatePart(month)}/${padReportDatePart(day)}/${year}`;
  }

  return trimmed;
};

export const formatReportLocaleCount = (value, fallback = "--") =>
  value != null ? Number(value).toLocaleString() : fallback;

/** Return IDs to send to API when a subset is selected; empty selection means "all" */
export const getPartialIdsForApi = (selectedIds) => {
  if (!selectedIds.length) return [];
  return selectedIds;
};

export const getFilterPartialIdsForApi = (selectedIds) => getPartialIdsForApi(selectedIds);

const normalizeId = (id) => (id != null && id !== "" ? String(id) : "");

export const getCustomerOptionIds = (list) =>
  (Array.isArray(list) ? list : [])
    .map((item) => normalizeId(item?.customerId))
    .filter(Boolean);

export const getLocationOptionIds = (list) =>
  (Array.isArray(list) ? list : [])
    .map((item) => normalizeId(item?.id))
    .filter(Boolean);

export const getProviderOptionIds = (list) =>
  (Array.isArray(list) ? list : [])
    .map((item) => normalizeId(item?.therapistId))
    .filter(Boolean);

export const buildDashboardQueryParams = (state, options = {}) => {
  const {
    forceOverallIfCurrent = false,
    forceRtmIfHep = false,
    useRtmMonthRange = false,
  } = options;

  const therapistId = state.user?.profile?.id;
  const profileStr = state.user?.profile?.profile;
  const profiles = parseProfiles(profileStr);
  const showCustomerFilter = isSuperAdminProfile(profileStr);
  const { filters } = state.reportDashboard;
  // Report APIs always use last-applied filters; draft lives in selectedFilters until Apply
  const appliedFilters = state.reportDashboard.appliedFilters
    ?? state.reportDashboard.selectedFilters;

  const selectedCustomerIds = (appliedFilters.customerIds || []).map(normalizeId);
  const selectedLocationIds = (appliedFilters.locationIds || []).map(normalizeId);
  const selectedProviderIds = (appliedFilters.providerIds || []).map(normalizeId);

  const parentClinicIds = showCustomerFilter
    ? getFilterPartialIdsForApi(selectedCustomerIds)
    : [];
  const childClinicIds = getFilterPartialIdsForApi(selectedLocationIds);
  const providerIds = getFilterPartialIdsForApi(selectedProviderIds);

  const { patientEngagementThresholds } = state.reportDashboard;

  let accountType = appliedFilters.accountType;
  let timeframe = appliedFilters.timeframe;
  let startDate = appliedFilters.startDate;
  let endDate = appliedFilters.endDate;

  if (timeframe === "CUSTOM") {
    if (useRtmMonthRange) {
      // RTM Claims reads only `month` — never the calendar range from other pages.
      // No month yet (arrived with a date range) → first month option, non-mutating.
      const monthId = appliedFilters.month || getDefaultRtmClaimsMonthId();
      const range = getRtmClaimsMonthDateRange(monthId);
      startDate = range.startDate;
      endDate = range.endDate;
    } else if (!hasCalendarDateRange(appliedFilters)) {
      // Calendar pages read only startDate/endDate — never the RTM month.
      // Only a month is set (came from RTM) → fall back to this page's default timeframe.
      timeframe = "CURRENT";
      startDate = null;
      endDate = null;
    }
  }

  // RTM Claims / Discharge Feedback have no Current — APIs use Overall instead
  if (forceOverallIfCurrent && timeframe === "CURRENT") {
    timeframe = "OVERALL";
    startDate = null;
    endDate = null;
  }

  // Provider Engagement / RTM Claims have no HEP — APIs use RTM instead
  if (forceRtmIfHep && accountType === "HEP") {
    accountType = "RTM";
  }

  return {
    therapistId,
    profiles,
    parentClinicIds,
    childClinicIds,
    providerIds,
    accountType,
    timeframe,
    startDate,
    endDate,
    minAdherence: patientEngagementThresholds?.min ?? null,
    maxAdherence: patientEngagementThresholds?.max ?? null,
  };
};

/** Build query string for reports/dashboard/* endpoints */
export const buildDashboardQuery = ({
  therapistId,
  profiles = [],
  parentClinicIds = [],
  childClinicIds = [],
  providerIds = [],
  accountType,
  timeframe,
  startDate,
  endDate,
  minAdherence,
  maxAdherence,
  includeAdherenceParams = false,
  pageNo,
  pageSize,
  eligibleOnly,
  downloadable,
  sortBy,
  sortDirection,
}) => {
  const parts = [`therapistId=${encodeURIComponent(therapistId)}`];
  profiles.forEach((p) => parts.push(`profile=${encodeURIComponent(p)}`));
  parentClinicIds.forEach((id) => parts.push(`parentClinicIds=${encodeURIComponent(id)}`));
  childClinicIds.forEach((id) => parts.push(`childClinicIds=${encodeURIComponent(id)}`));
  providerIds.forEach((id) => parts.push(`providerIds=${encodeURIComponent(id)}`));
  parts.push(`accountType=${encodeURIComponent(accountType || "ALL")}`);
  if (timeframe) {
    parts.push(`timeframe=${encodeURIComponent(timeframe)}`);
  }
  if (timeframe === "CUSTOM") {
    if (startDate) parts.push(`startDate=${encodeURIComponent(startDate)}`);
    if (endDate) parts.push(`endDate=${encodeURIComponent(endDate)}`);
  }
  if (includeAdherenceParams) {
    if (minAdherence != null) {
      parts.push(`minAdherence=${encodeURIComponent(minAdherence)}`);
    }
    if (maxAdherence != null) {
      parts.push(`maxAdherence=${encodeURIComponent(maxAdherence)}`);
    }
  }
  if (pageNo != null) {
    parts.push(`pageNo=${encodeURIComponent(pageNo)}`);
  }
  if (pageSize != null) {
    parts.push(`pageSize=${encodeURIComponent(pageSize)}`);
  }
  if (eligibleOnly != null) {
    parts.push(`eligibleOnly=${encodeURIComponent(eligibleOnly)}`);
  }
  if (downloadable != null) {
    parts.push(`downloadable=${encodeURIComponent(downloadable)}`);
  }
  if (sortBy != null && sortBy !== "") {
    parts.push(`sortBy=${encodeURIComponent(sortBy)}`);
  }
  if (sortDirection != null && sortDirection !== "") {
    parts.push(`sortDirection=${encodeURIComponent(sortDirection)}`);
  }
  return `?${parts.join("&")}`;
};

export const parsePatientsVolumeSummaryResponse = (response) => {
  const data = response?.data?.data ?? response?.data ?? {};
  return {
    totalRtmClaims: data.totalRtmClaims ?? null,
  };
};

export const parseProviderTimeSummaryResponse = (response) => {
  const data = response?.data?.data ?? response?.data ?? {};
  return {
    totalProviderTimeHours: data.totalProviderTimeHours ?? null,
  };
};

export { parseRtmClaimsResponse } from "./rtmClaims";

export const parsePatientEngagementResponse = (response) => {
  const data = response?.data?.data ?? response?.data ?? {};
  return {
    totalPatients: data.totalPatients ?? null,
    averageAdherence: data.averageAdherence ?? null,
    minAdherenceThreshold: data.minAdherenceThreshold ?? null,
    maxAdherenceThreshold: data.maxAdherenceThreshold ?? null,
    adherence: data.adherence ?? null,
    appliedFilters: data.appliedFilters ?? null,
  };
};

export const parseAdherenceTrendResponse = (response) => {
  const data = response?.data?.data ?? response?.data ?? {};
  const points = Array.isArray(data.points) ? data.points : [];
  return {
    points: points.map((point) => ({
      date: point?.date ?? null,
      averageAdherence: point?.averageAdherence ?? null,
    })),
    appliedFilters: data.appliedFilters ?? null,
  };
};

export const parseDischargeFeedbackNpsResponse = (response) => {
  const data = response?.data?.data ?? response?.data ?? {};
  return {
    netPromoterScore: data.netPromoterScore ?? null,
    npsCategories: {
      promoters: data.npsCategories?.promoters ?? {},
      passives: data.npsCategories?.passives ?? {},
      detractors: data.npsCategories?.detractors ?? {},
    },
    scoreDistribution: data.scoreDistribution ?? {},
    appliedFilters: data.appliedFilters ?? null,
  };
};

export const parseDischargeFeedbackOutcomesResponse = (response) => {
  const data = response?.data?.data ?? response?.data ?? {};
  const outcomes = data.dischargeOutcomes ?? {};
  return {
    totalResponses: outcomes.totalResponses ?? null,
    yes: outcomes.yes ?? {},
    no: outcomes.no ?? {},
    appliedFilters: data.appliedFilters ?? null,
  };
};

export const parseDischargeFeedbackOutcomesTrendResponse = (response) => {
  const data = response?.data?.data ?? response?.data ?? {};
  const points = Array.isArray(data.points) ? data.points : [];
  return {
    points: points.map((point) => ({
      date: point?.date ?? null,
      yesCount: point?.yesCount ?? null,
      yesPercentage: point?.yesPercentage ?? null,
      noCount: point?.noCount ?? null,
      noPercentage: point?.noPercentage ?? null,
    })),
    appliedFilters: data.appliedFilters ?? null,
  };
};

export const parseDischargeFeedbackNpsTrendResponse = (response) => {
  const data = response?.data?.data ?? response?.data ?? {};
  const points = Array.isArray(data.points) ? data.points : [];
  return {
    points: points.map((point) => ({
      date: point?.date ?? null,
      score: point?.score ?? null,
    })),
    appliedFilters: data.appliedFilters ?? null,
  };
};

export const parseProfiles = (profileStr) =>
  (profileStr || "")
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);

export const isSuperAdminProfile = (profileStr) =>
  parseProfiles(profileStr).includes("SUPER_ADMIN");

export const FILTER_VALUES_PAGE_SIZE = 20;

/** Build query string for reports/filter-values/* endpoints */
export const buildFilterValuesQuery = ({
  therapistId,
  profiles = [],
  parentClinicIds = [],
  childClinicIds = [],
  pageNo = 0,
  pageSize = FILTER_VALUES_PAGE_SIZE,
  filter,
}) => {
  const parts = [
    `therapistId=${encodeURIComponent(therapistId)}`,
    `pageNo=${encodeURIComponent(pageNo)}`,
    `pageSize=${encodeURIComponent(pageSize)}`,
  ];
  profiles.forEach((p) => parts.push(`profile=${encodeURIComponent(p)}`));
  parentClinicIds.forEach((id) => parts.push(`parentClinicIds=${encodeURIComponent(id)}`));
  childClinicIds.forEach((id) => parts.push(`childClinicIds=${encodeURIComponent(id)}`));
  if (filter != null && String(filter).trim() !== "") {
    parts.push(`filter=${encodeURIComponent(String(filter).trim())}`);
  }
  return `?${parts.join("&")}`;
};

export const parseFilterPageResponse = (response) => {
  const body = response?.data?.data ?? response?.data ?? {};
  const list = Array.isArray(body.pageContent)
    ? body.pageContent
    : extractFilterList(body);
  const pageNo = body.pageNo ?? 0;
  const totalPage = body.totalPage ?? 1;
  return {
    list,
    pageNo,
    totalPage,
    totalRecords: body.totalRecords ?? list.length,
    hasMore: pageNo + 1 < totalPage,
  };
};

export const extractFilterList = (responseData, listKey) => {
  if (!responseData) return [];

  const singularKey = listKey?.endsWith("s") ? listKey.slice(0, -1) : listKey;

  const findArray = (obj) => {
    if (!obj) return null;
    if (Array.isArray(obj)) return obj;
    if (Array.isArray(obj[listKey])) return obj[listKey];
    if (singularKey && Array.isArray(obj[singularKey])) return obj[singularKey];
    if (Array.isArray(obj.data)) return obj.data;
    if (obj.data && typeof obj.data === "object") {
      const nested = findArray(obj.data);
      if (nested) return nested;
    }
    if (Array.isArray(obj.list)) return obj.list;
    if (Array.isArray(obj.pageContent)) return obj.pageContent;
    if (Array.isArray(obj.items)) return obj.items;
    if (Array.isArray(obj.values)) return obj.values;
    if (Array.isArray(obj.filterValues)) return obj.filterValues;
    if (typeof obj === "object") {
      const firstArray = Object.values(obj).find((v) => Array.isArray(v) && v.length > 0);
      if (firstArray) return firstArray;
    }
    return null;
  };

  return findArray(responseData) ?? findArray(responseData?.data) ?? [];
};
